import { useState } from 'react';
import {
  Box,
  Button,
  DialogActions,
  Typography,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import { WbCardContent } from '../../../WbCard';
import { WbButton } from '../../../WbButton';
import { useGeneralPurposePickerContext } from '../../GeneralPurposePickerContext';
import {
  GenericEntityType,
  KindEnum,
  Provider,
  TemplateEntity,
  TextFieldValue,
} from '../../types';
import { ProviderFilters } from '../CommonFilter';
import { EntityKindSelector } from '../EntityKindSelector';
import { SelectableEntityTable } from '../SelectableEntityTable';
import { WbNoData } from '../../../WbNoData';
import { selectTemplateEntity } from '../utils';
import {
  WbDockableGroup,
  WbDockablePanel,
  WbDockableResizeHandle,
} from '../../../WbDockable';
import { SelectedEntities } from './SelectedEntities';
import { useWittyTable } from '@agilelab/plugin-wb-witty-react';

const useStyles = makeStyles(
  () => ({
    cardContent: {
      overflow: 'auto',
      padding: '0px',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px 0px',
      alignItems: 'center',
    },
  }),
  { name: 'EntityListMultiPicker' },
);

interface MultiSelectionProps {
  providerMapper: Map<string, Provider<GenericEntityType[], ProviderFilters>>;
  selectedEntityProvider?: Provider<GenericEntityType[], ProviderFilters>;
  availableKinds: TemplateEntity[];
  parentTemplateRef?: string;
}

export const MultipleSelectionTable = ({
  selectedEntityProvider,
  entities,
  setEntities,
}: {
  selectedEntityProvider: Provider<GenericEntityType[], ProviderFilters>;
  entities: TextFieldValue[];
  setEntities: React.Dispatch<React.SetStateAction<TextFieldValue[]>>;
}) => {
  const context = selectedEntityProvider?.useContext();
  const wittyTable = useWittyTable();

  return (
    <SelectableEntityTable
      multiple
      columns={context?.columns ?? []}
      loading={context?.entitiesState.loading ?? false}
      data={(context?.entitiesState.value as GenericEntityType[]) ?? []}
      adapter={selectedEntityProvider?.adapt!}
      entities={entities}
      onChangeHandler={setEntities}
      filtersSection={selectedEntityProvider?.filtersSection!}
      pagination={context?.pagination!}
      setPagination={context?.setPagination!}
      count={context?.count ?? 0}
      nextCursor={context?.nextCursor}
      prevCursor={context?.prevCursor}
      templateEntity={context?.templateKind!}
      isAiMatchAvailable={
        (context.templateKind?.type === KindEnum.remote &&
          context.templateKind?.wittySuggestions?.enabled &&
          wittyTable !== undefined) ??
        false
      }
      getAllFilteredEntities={(context as any)?.getAllFilteredEntities}
    />
  );
};

export const MultiSelection: React.FC<MultiSelectionProps> = ({
  availableKinds,
  parentTemplateRef,
}) => {
  const theme = useTheme();
  const classes = useStyles();

  const {
    setSelectedValues,
    setOpenDialog,
    selectedKind,
    selectedValues,
    formContext,
    selectedEntityProvider,
  } = useGeneralPurposePickerContext();

  const [entities, setEntities] = useState<TextFieldValue[]>(
    (selectedValues?.tableValues as TextFieldValue[]) ?? [],
  );

  const renderedComponent = selectedEntityProvider?.renderContextProvider(
    selectTemplateEntity(availableKinds, selectedKind),
    <MultipleSelectionTable
      selectedEntityProvider={selectedEntityProvider}
      entities={entities}
      setEntities={setEntities}
    />,
    parentTemplateRef,
    formContext,
  );

  return (
    <WbCardContent
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '64vh',
        padding: 0,
      }}
    >
      <WbDockableGroup direction="horizontal">
        <WbDockablePanel defaultSize={70}>
          <WbCardContent className={classes.cardContent}>
            <Box className={classes.header}>
              <Typography variant="body1"> Search for entities</Typography>
              {availableKinds.length > 1 && (
                <EntityKindSelector availableKinds={availableKinds} />
              )}
            </Box>
            {renderedComponent ?? <WbNoData />}
          </WbCardContent>
        </WbDockablePanel>

        <WbDockableResizeHandle
          showIconOnHover
          divider
          size={4}
          direction="horizontal"
        />

        <WbDockablePanel maxSize={30}>
          <SelectedEntities
            entities={entities ?? []}
            setEntities={setEntities}
            selectableEntities={availableKinds.length}
          />
        </WbDockablePanel>
      </WbDockableGroup>

      <DialogActions
        style={{
          height: '56px',
          position: 'sticky',
          bottom: 0,
          borderTop: `1px solid ${theme.palette.grey[300]}`,
          background: theme.palette.white,
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => {
            setOpenDialog(false);
          }}
        >
          Cancel
        </Button>
        <WbButton
          onClick={() => {
            setSelectedValues(preValue => {
              return {
                tableValues: entities.map(entity => {
                  const { isAiGenerated, ...rest } = entity;
                  return rest;
                }),
                arbitraryValues: preValue?.arbitraryValues ?? [],
              };
            });
            setOpenDialog(false);
          }}
          variant="contained"
          size="small"
          color="primary"
          label="Apply"
        />
      </DialogActions>
    </WbCardContent>
  );
};
